/**
 * Minimal colour maths for deriving a themed palette from the admin's brand colour.
 *
 * The brand colour is configurable, so every accent has to be *derived* at runtime rather than
 * hardcoded. The important one is `forDarkSurface`: a saturated mid-tone brand (like the default
 * gold #e1b137) is fine on white but reads as loud and slightly muddy on a dark canvas, so dark
 * mode gets a lifted, slightly desaturated variant instead of the raw brand.
 */

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

export function hexToRgb(hex) {
    let h = String(hex || "").replace("#", "").trim();
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    if (h.length !== 6 || /[^0-9a-f]/i.test(h)) return null;
    return {
        r: parseInt(h.slice(0, 2), 16),
        g: parseInt(h.slice(2, 4), 16),
        b: parseInt(h.slice(4, 6), 16),
    };
}

export const rgbToHex = ({r, g, b}) =>
    "#" + [r, g, b].map((v) => clamp(Math.round(v), 0, 255).toString(16).padStart(2, "0")).join("");

export function rgbToHsl({r, g, b}) {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h = 0, s = 0;
    const l = (max + min) / 2;
    const d = max - min;
    if (d !== 0) {
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: h = ((g - b) / d + (g < b ? 6 : 0)); break;
            case g: h = ((b - r) / d + 2); break;
            default: h = ((r - g) / d + 4);
        }
        h /= 6;
    }
    return {h: h * 360, s, l};
}

export function hslToRgb({h, s, l}) {
    h = ((h % 360) + 360) % 360 / 360;
    if (s === 0) {
        const v = Math.round(l * 255);
        return {r: v, g: v, b: v};
    }
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    const hue = (t) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
    };
    return {
        r: Math.round(hue(h + 1 / 3) * 255),
        g: Math.round(hue(h) * 255),
        b: Math.round(hue(h - 1 / 3) * 255),
    };
}

const adjust = (hex, fn, fallback) => {
    const rgb = hexToRgb(hex);
    if (!rgb) return fallback;
    const hsl = rgbToHsl(rgb);
    return rgbToHex(hslToRgb(fn(hsl)));
};

/** Relative luminance (WCAG) — used to decide readable foreground on a brand fill. */
export function luminance(hex) {
    const rgb = hexToRgb(hex);
    if (!rgb) return 0;
    const ch = [rgb.r, rgb.g, rgb.b]
        .map((v) => v / 255)
        .map((v) => (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)));
    return 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2];
}

/** Near-black or near-white text, whichever stays readable on the given fill. */
export const readableOn = (hex) => (luminance(hex) > 0.45 ? "#10131a" : "#ffffff");

export const lighten = (hex, amount, fallback = hex) =>
    adjust(hex, (hsl) => ({...hsl, l: clamp(hsl.l + amount, 0, 1)}), fallback);

export const darken = (hex, amount, fallback = hex) =>
    adjust(hex, (hsl) => ({...hsl, l: clamp(hsl.l - amount, 0, 1)}), fallback);

export const desaturate = (hex, amount, fallback = hex) =>
    adjust(hex, (hsl) => ({...hsl, s: clamp(hsl.s - amount, 0, 1)}), fallback);

/**
 * The brand colour adapted for a dark canvas: lifted so it separates from the background and
 * slightly desaturated so it reads as a considered accent rather than a highlighter.
 */
export function forDarkSurface(hex) {
    const rgb = hexToRgb(hex);
    if (!rgb) return hex;
    const {h, s, l} = rgbToHsl(rgb);
    return rgbToHex(hslToRgb({
        h,
        s: clamp(s * 0.82, 0, 0.72),
        // Pull very dark or very light brands toward a comfortable mid-high band.
        l: clamp(l < 0.5 ? l + 0.16 : Math.min(l, 0.68), 0.42, 0.72),
    }));
}

/** A translucent version of a colour, for tinted fills and focus rings. */
export function alpha(hex, a) {
    const rgb = hexToRgb(hex);
    if (!rgb) return hex;
    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${a})`;
}
